import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { RichTextEditor } from "./rich-text-editor";
import type { TiptapDoc } from "./rich-text-doc";
import { IssueTypeIcon } from "./task-icons";
import { ErrorBanner, RequiredMark, useModalA11y } from "./ui-kit";

type HierarchyTask = {
  assigneeName: string;
  description: TiptapDoc | null;
  estimatePoints: number | null;
  id: string;
  parentId: string | null;
  status: string;
  taskKey: string;
  title: string;
  type: "epic" | "story" | "task" | "subtask" | "bug";
  updatedAt: string;
};

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

export function TaskHierarchy({
  initialQuery = "",
  isAdmin,
  onQueryChange,
  teamId,
}: {
  initialQuery?: string;
  isAdmin: boolean;
  onQueryChange?: (query: string) => void;
  teamId: string;
}) {
  const [tasks, setTasks] = useState<HierarchyTask[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [newDescription, setNewDescription] = useState<TiptapDoc | null>(null);
  const [editing, setEditing] = useState<HierarchyTask | null>(null);
  const [editDescription, setEditDescription] = useState<TiptapDoc | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState<"updated" | "key" | "title">("updated");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const load = useCallback(async () => {
    const result = await api<{ permissions: string[]; tasks: HierarchyTask[] }>(`/api/tasks?teamId=${encodeURIComponent(teamId)}`);
    setTasks(result.tasks); setPermissions(result.permissions);
  }, [teamId]);
  useEffect(() => { void load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load task hierarchy.")); }, [load]);
  useEffect(() => setQuery(initialQuery), [initialQuery]);
  const canManage = isAdmin || permissions.includes("manage_tasks");
  const taskById = useMemo(() => new Map(tasks.map((task) => [task.id, task])), [tasks]);
  const childrenByParent = useMemo(() => {
    const result = new Map<string, HierarchyTask[]>();
    for (const task of tasks) if (task.parentId) result.set(task.parentId, [...(result.get(task.parentId) ?? []), task]);
    return result;
  }, [tasks]);
  const descendants = useCallback((id: string): HierarchyTask[] => {
    const direct = childrenByParent.get(id) ?? [];
    return direct.flatMap((child) => [child, ...descendants(child.id)]);
  }, [childrenByParent]);
  const parents = useMemo(() => tasks
    .filter((task) => task.type === "epic")
    .filter((task) => !status || task.status === status)
    .filter((task) => !query || `${task.taskKey} ${task.title} ${task.assigneeName}`.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
    .sort((left, right) => sort === "key" ? left.taskKey.localeCompare(right.taskKey) : sort === "title" ? left.title.localeCompare(right.title) : right.updatedAt.localeCompare(left.updatedAt)), [query, sort, status, tasks]);
  const createParent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/tasks", { body: JSON.stringify({ description: newDescription, teamId, title: form.get("title"), type: "epic" }), method: "POST" });
      setCreating(false); await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not create parent."); }
    finally { setBusy(false); }
  };
  const saveTask = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); if (!editing) return;
    setBusy(true); setError(""); const form = new FormData(event.currentTarget);
    try {
      await api(`/api/tasks/${editing.id}`, { body: JSON.stringify({ description: editDescription, parentId: form.get("parentId") || null, title: form.get("title") }), method: "PATCH" });
      setEditing(null); await load();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not update work item."); }
    finally { setBusy(false); }
  };
  const deleteSelected = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setBusy(true); setError(""); setNotice("");
    const form = new FormData(event.currentTarget);
    const targetId = String(form.get("childDestination") ?? "") || null;
    const failures: string[] = [];
    for (const parentId of selected) {
      const parent = taskById.get(parentId);
      if (!parent) continue;
      try {
        for (const child of childrenByParent.get(parentId) ?? []) {
          await api(`/api/tasks/${child.id}`, { body: JSON.stringify({ parentId: targetId }), method: "PATCH" });
        }
        await api(`/api/tasks/${parentId}`, { method: "DELETE" });
      } catch (reason) {
        failures.push(`${parent.taskKey}: ${reason instanceof Error ? reason.message : "delete failed"}`);
      }
    }
    await load(); setDeleteOpen(false); setBusy(false);
    setSelected(new Set(failures.map((failure) => tasks.find(({ taskKey }) => failure.startsWith(`${taskKey}:`))?.id).filter(Boolean) as string[]));
    if (failures.length) setError(`Some parents could not be deleted. ${failures.join(" ")}`);
    else setNotice("Selected parents were deleted; their child tasks were preserved.");
  };
  const renderNode = (task: HierarchyTask, depth = 0): React.ReactNode => {
    const children = childrenByParent.get(task.id) ?? [];
    const open = expanded.has(task.id);
    const path: string[] = [];
    let current: HierarchyTask | undefined = task;
    while (current) { path.unshift(current.taskKey); current = current.parentId ? taskById.get(current.parentId) : undefined; }
    return <li key={task.id}>
      <article className="hierarchy-node" style={{ marginLeft: `${Math.min(depth, 6) * 24}px` }}>
        <button aria-expanded={open} aria-label={`${open ? "Collapse" : "Expand"} ${task.taskKey}`} className="tree-toggle" disabled={!children.length} onClick={() => setExpanded((value) => { const next = new Set(value); if (next.has(task.id)) next.delete(task.id); else next.add(task.id); return next; })} type="button">{children.length ? (open ? "−" : "+") : "·"}</button>
        <span className="task-type"><IssueTypeIcon type={task.type} /> {task.type}</span><strong>{task.taskKey}</strong><div><h4>{task.title}</h4><p>{task.assigneeName} · {task.status.replace("_", " ")} · {path.join(" › ")}</p></div>
        {canManage && <button onClick={() => { setEditDescription(task.description); setEditing(task); }} type="button">Edit</button>}
      </article>
      {children.length && open ? <ul>{children.map((child) => renderNode(child, depth + 1))}</ul> : null}
    </li>;
  };
  return (
    <section className="hierarchy-page">
      <div className="section-heading"><div><p className="eyebrow">Task structure</p><h2>Parents and hierarchy</h2><p className="section-description">Manage every Epic and inspect all nested descendants without flattening the work.</p></div>{canManage && <button className="button primary" onClick={() => { setNewDescription(null); setCreating(true); }} type="button">+ Create parent</button>}</div>
      <div className="hierarchy-toolbar"><label>Search parents<input aria-label="Search parents" onChange={(event) => { setQuery(event.target.value); onQueryChange?.(event.target.value); }} placeholder="Key, title, or owner" value={query} /></label><label>Status<select onChange={(event) => setStatus(event.target.value)} value={status}><option value="">All statuses</option><option value="open">To do</option><option value="in_progress">In progress</option><option value="done">Done</option></select></label><label>Sort<select onChange={(event) => setSort(event.target.value as typeof sort)} value={sort}><option value="updated">Recently updated</option><option value="key">Key</option><option value="title">Title</option></select></label></div>
      <ErrorBanner>{error}</ErrorBanner>{notice && <p className="banner-notice">{notice}</p>}
      <section className="parent-directory"><div className="subsection-heading"><div><h3>Parent directory</h3><p>{parents.length} visible Epic{parents.length === 1 ? "" : "s"}</p></div>{canManage && parents.length > 0 && <div className="bulk-actions"><button className="button secondary" onClick={() => setSelected(new Set(selected.size === parents.length ? [] : parents.map(({ id }) => id)))} type="button">{selected.size === parents.length ? "Clear selection" : "Select all visible"}</button><button className="button danger" disabled={!selected.size} onClick={() => setDeleteOpen(true)} type="button">Delete selected</button></div>}</div>{parents.length ? <div>{parents.map((parent) => { const nested = descendants(parent.id); const completed = nested.filter(({ status: childStatus }) => childStatus === "done").length; return <article className="parent-directory-row" key={parent.id}>{canManage && <input aria-label={`Select ${parent.taskKey}`} checked={selected.has(parent.id)} onChange={(event) => setSelected((value) => { const next = new Set(value); if (event.target.checked) next.add(parent.id); else next.delete(parent.id); return next; })} type="checkbox" />}<div><span className="task-type"><IssueTypeIcon type="epic" /> Epic</span><strong>{parent.taskKey}</strong><h4>{parent.title}</h4></div><dl><div><dt>Status</dt><dd>{parent.status.replace("_", " ")}</dd></div><div><dt>Owner</dt><dd>{parent.assigneeName}</dd></div><div><dt>Children</dt><dd>{completed}/{nested.length} complete</dd></div><div><dt>Estimate</dt><dd>{nested.reduce((sum, child) => sum + Number(child.estimatePoints ?? 0), 0)} pt</dd></div><div><dt>Updated</dt><dd>{new Date(parent.updatedAt).toLocaleDateString()}</dd></div></dl><div><button className="button secondary" onClick={() => setExpanded((value) => new Set(value).add(parent.id))} type="button">View descendants</button>{canManage && <button className="text-button" onClick={() => { setEditDescription(parent.description); setEditing(parent); }} type="button">Edit</button>}</div>{expanded.has(parent.id) && <ul className="inline-descendants">{(childrenByParent.get(parent.id) ?? []).map((child) => renderNode(child, 1))}</ul>}</article>; })}</div> : <div className="empty-state compact"><h2>{query || status ? "No matching parents" : "No parents yet"}</h2><p>{query || status ? "Clear or adjust the current filters." : "Create an Epic to organize related work."}</p></div>}</section>
      {creating && (
        <CreateParentDialog
          busy={busy}
          description={newDescription}
          onClose={() => setCreating(false)}
          onDescriptionChange={setNewDescription}
          onSubmit={(event) => void createParent(event)}
        />
      )}
      {editing && (
        <EditParentDialog
          busy={busy}
          description={editDescription}
          editing={editing}
          onClose={() => setEditing(null)}
          onDescriptionChange={setEditDescription}
          onSubmit={(event) => void saveTask(event)}
          tasks={tasks}
        />
      )}
      {deleteOpen && (
        <DeleteParentsDialog
          busy={busy}
          onClose={() => setDeleteOpen(false)}
          onSubmit={(event) => void deleteSelected(event)}
          selectedCount={selected.size}
          targetOptions={tasks.filter((task) => task.type === "epic" && !selected.has(task.id))}
        />
      )}
    </section>
  );
}

function CreateParentDialog({
  busy,
  description,
  onClose,
  onDescriptionChange,
  onSubmit,
}: {
  busy: boolean;
  description: TiptapDoc | null;
  onClose: () => void;
  onDescriptionChange: (value: TiptapDoc) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-modal="true" className="dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">Hierarchy</p>
        <h2>Create parent</h2>
        <form onSubmit={onSubmit}>
          <label><span>Title<RequiredMark /></span><input autoFocus name="title" required /></label>
          <p className="admin-help">Top-level parents are Epics. Stories, tasks, bugs, and subtasks remain nested children.</p>
          <label>Description<RichTextEditor ariaLabel="Parent description" characterLimit={10000} onChange={onDescriptionChange} placeholder="Add a description" value={description} /></label>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">Create parent</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function EditParentDialog({
  busy,
  description,
  editing,
  onClose,
  onDescriptionChange,
  onSubmit,
  tasks,
}: {
  busy: boolean;
  description: TiptapDoc | null;
  editing: HierarchyTask;
  onClose: () => void;
  onDescriptionChange: (value: TiptapDoc) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  tasks: HierarchyTask[];
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-modal="true" className="dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">{editing.taskKey}</p>
        <h2>Edit work item</h2>
        <form onSubmit={onSubmit}>
          <label><span>Title<RequiredMark /></span><input autoFocus defaultValue={editing.title} name="title" required /></label>
          <label>Description<RichTextEditor ariaLabel="Work item description" characterLimit={10000} onChange={onDescriptionChange} placeholder="Add a description" taskId={editing.id} value={description} /></label>
          {editing.type !== "epic" && (
            <label>
              Parent
              <select defaultValue={editing.parentId ?? ""} name="parentId">
                <option value="">No parent</option>
                {tasks.filter(({ id }) => id !== editing.id).map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>{candidate.taskKey} · {candidate.title}</option>
                ))}
              </select>
            </label>
          )}
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">Save</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function DeleteParentsDialog({
  busy,
  onClose,
  onSubmit,
  selectedCount,
  targetOptions,
}: {
  busy: boolean;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  selectedCount: number;
  targetOptions: HierarchyTask[];
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-modal="true" className="dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">Destructive action</p>
        <h2>Delete {selectedCount} parent{selectedCount === 1 ? "" : "s"}</h2>
        <p>Parent tasks will be deleted. Child tasks will be preserved using the choice below.</p>
        <form onSubmit={onSubmit}>
          <label>
            Child handling
            <select name="childDestination">
              <option value="">Leave direct children unparented</option>
              {targetOptions.map((task) => (
                <option key={task.id} value={task.id}>Move direct children to {task.taskKey} · {task.title}</option>
              ))}
            </select>
          </label>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button danger" disabled={busy} type="submit">Delete parents</button>
          </div>
        </form>
      </section>
    </div>
  );
}
